import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { type ContentItem, adminFetchContent, adminSaveContent, adminSaveContentBatch } from '@/api/client';

interface ContentField {
  id: string;
  label: string;
  value: string;
  type: 'text' | 'textarea' | 'tel' | 'email';
  placeholder: string;
  hint: string;
}

const BUSINESS_DEFAULTS: Record<string, string> = {
  mainPhone: '7076236359',
  mainEmail: 'info@completehomecarecleaning.com',
  businessAddress: 'PO Box 115, Windsor, CA 95492',
  weekday_hours: '24 Hours',
  companyName: 'Complete Home Care Cleaning',
};

const buildEmptyFields = (t: (k: string) => string): ContentField[] => [
  // ── Contact & Business Info ──────────────────────────────
  {
    id: 'companyName',
    label: t('admin_company_name'),
    value: BUSINESS_DEFAULTS.companyName,
    type: 'text',
    placeholder: 'e.g. Complete Home Care Cleaning',
    hint: t('admin_content_hint'),
  },
  {
    id: 'mainPhone',
    label: t('admin_phone_number'),
    value: BUSINESS_DEFAULTS.mainPhone,
    type: 'tel',
    placeholder: '7076236359',
    hint: 'Shown in the header, contact section, and footer',
  },
  {
    id: 'mainEmail',
    label: t('admin_email_address'),
    value: BUSINESS_DEFAULTS.mainEmail,
    type: 'email',
    placeholder: 'info@completehomecarecleaning.com',
    hint: 'Shown in the contact section and footer',
  },
  {
    id: 'businessAddress',
    label: 'Business Address',
    value: BUSINESS_DEFAULTS.businessAddress,
    type: 'text',
    placeholder: 'PO Box 115, Windsor, CA 95492',
    hint: 'Mailing / physical address shown in the contact section',
  },
  {
    id: 'weekday_hours',
    label: 'Business Hours',
    value: BUSINESS_DEFAULTS.weekday_hours,
    type: 'text',
    placeholder: 'e.g. 24 Hours  or  Mon – Fri: 8am – 6pm',
    hint: 'Shown in the Contact section under the clock icon',
  },
  // ── Hero / Landing Content ───────────────────────────────
  {
    id: 'heroCta',
    label: t('admin_hero_cta_hint'),
    value: '',
    type: 'text',
    placeholder: t('admin_placeholder_hero_cta'),
    hint: t('admin_hero_cta_hint'),
  },
  {
    id: 'aboutTitle',
    label: t('admin_about_title_hint'),
    value: '',
    type: 'text',
    placeholder: t('admin_placeholder_about_title'),
    hint: t('admin_about_title_hint'),
  },
  {
    id: 'aboutText',
    label: t('admin_about_text_hint'),
    value: '',
    type: 'textarea',
    placeholder: t('admin_placeholder_about_text'),
    hint: t('admin_about_text_hint'),
  },
  {
    id: 'footerText',
    label: t('admin_footer_hint'),
    value: '',
    type: 'textarea',
    placeholder: t('admin_placeholder_footer_text'),
    hint: t('admin_footer_hint'),
  },
];

export default function AdminContentManager() {
  const { t } = useTranslation('home');
  const [content, setContent] = useState<ContentField[]>(buildEmptyFields(t));
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [lang, setLang] = useState('en');

  const loadContent = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const items = await adminFetchContent(lang);
      // Build a map from backend response
      const map: Record<string, string> = {};
      for (const item of items) {
        if (item.content_key) {
          map[item.content_key] = item.content_value || '';
        }
      }
      // Merge: if backend has a value use it, otherwise keep the local default
      setContent((prev) =>
        prev.map((field) => ({
          ...field,
          value: map[field.id] !== undefined ? map[field.id] : field.value,
        })),
      );
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const handleEdit = (field: ContentField) => {
    setEditingId(field.id);
    setEditValue(field.value);
  };

  const handleSaveField = async () => {
    if (editingId === null) return;
    setSaving(true);
    const res = await adminSaveContent({
      lang,
      content_key: editingId,
      content_value: editValue,
    });
    setSaving(false);
    if (res.success) {
      setContent((prev) => prev.map((f) => (f.id === editingId ? { ...f, value: editValue } : f)));
      setEditingId(null);
      setHasChanges(true);
      Swal.fire({
        title: t('admin_api_saved'),
        icon: 'success',
        confirmButtonColor: '#3DBFA0',
        timer: 1500,
        timerProgressBar: true,
      });
    } else {
      Swal.fire({
        title: t('admin_api_error'),
        text: res.message || '',
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleSaveAll = async () => {
    setSaving(true);
    let anyFailed = false;
    for (const field of content) {
      const res = await adminSaveContent({
        lang,
        content_key: field.id,
        content_value: field.value,
      });
      if (!res.success) {
        anyFailed = true;
      }
    }
    setSaving(false);
    setHasChanges(false);
    if (anyFailed) {
      Swal.fire({
        title: t('admin_api_error'),
        text: 'Some fields could not be saved.',
        icon: 'warning',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    } else {
      Swal.fire({
        title: t('admin_all_saved'),
        icon: 'success',
        confirmButtonColor: '#3DBFA0',
        timer: 2500,
        timerProgressBar: true,
      });
    }
  };

  const handleReset = () => {
    Swal.fire({
      title: t('admin_reset_defaults'),
      text: t('admin_reset_confirm'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#1A2E4A',
      confirmButtonText: t('admin_reset_defaults'),
      cancelButtonText: t('admin_cancel'),
    }).then(async (result) => {
      if (result.isConfirmed) {
        setSaving(true);
        const emptyFields = buildEmptyFields(t);
        const items = emptyFields.map((field) => ({
          content_key: field.id,
          content_value: '',
        }));
        try {
          const saveRes = await adminSaveContentBatch({ lang, items });
          if (saveRes.success) {
            setContent(emptyFields);
            setHasChanges(false);
            Swal.fire({
              title: t('admin_reset_complete'),
              text: t('admin_reset_complete'),
              icon: 'success',
              confirmButtonColor: '#3DBFA0',
              timer: 1500,
              timerProgressBar: true,
            });
          } else {
            Swal.fire({
              title: t('admin_error'),
              text: saveRes.message || t('admin_save_error'),
              icon: 'error',
              confirmButtonColor: '#3DBFA0',
              confirmButtonText: t('admin_ok'),
            });
          }
        } catch {
          Swal.fire({
            title: t('admin_error'),
            text: t('admin_save_error'),
            icon: 'error',
            confirmButtonColor: '#3DBFA0',
            confirmButtonText: t('admin_ok'),
          });
        } finally {
          setSaving(false);
        }
      }
    });
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-brand-dark font-heading">
            {t('admin_content_manager')}
          </h2>
          <p className="text-sm text-brand-muted mt-1">{t('admin_edit_content')}</p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="text-xs text-amber-600 font-medium">
              <i className="ri-error-warning-line mr-1" />
              {t('admin_unsaved')}
            </span>
          )}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setLang('en')}
              className={`px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors ${lang === 'en' ? 'bg-brand-mint text-white' : 'bg-white text-brand-muted hover:bg-gray-50'}`}
            >
              EN
            </button>
            <button
              onClick={() => setLang('es')}
              className={`px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors ${lang === 'es' ? 'bg-brand-mint text-white' : 'bg-white text-brand-muted hover:bg-gray-50'}`}
            >
              ES
            </button>
          </div>
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-brand-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line" />
            {t('admin_reset_defaults')}
          </button>
        </div>
      </div>

      {/* Info banner: what syncs to the public site */}
      {!loading && !fetchError && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-brand-mint/20 bg-brand-light/30">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-mint/10 shrink-0">
            <i className="ri-information-line text-brand-mint text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-brand-dark">How Page Content syncs</p>
            <p className="text-xs text-brand-muted mt-0.5 leading-relaxed">
              Fields saved here are stored in the backend database and override the landing page defaults.
              Pre-filled values (phone, email, address, hours) reflect the current business data.
              If the landing page still shows old values, ask the developer to wire the frontend to read from
              <code className="mx-1 px-1 py-0.5 bg-gray-100 rounded text-[10px] font-mono">/api/content/get.php</code>
              for each key.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center">
          <div className="inline-flex flex-col items-center gap-3">
            <i className="ri-loader-4-line animate-spin text-3xl text-brand-mint" />
            <p className="text-sm text-brand-muted">{t('admin_loading_data')}</p>
          </div>
        </div>
      ) : fetchError ? (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-red-100 bg-red-50">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 shrink-0">
            <i className="ri-wifi-off-line text-red-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800">Backend not reachable</p>
            <p className="text-xs text-red-700 mt-0.5">{fetchError}</p>
          </div>
          <button
            onClick={loadContent}
            className="shrink-0 px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />Retry
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {content.map((field) => (
            <div
              key={field.id}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-brand-mint/20 transition-colors"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-brand-light/60 flex items-center justify-center shrink-0">
                    <i
                      className={`${field.type === 'textarea' ? 'ri-article-line' : field.type === 'email' ? 'ri-mail-line' : field.type === 'tel' ? 'ri-phone-line' : 'ri-text'} text-brand-mint text-sm`}
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-brand-dark">{field.label}</h3>
                    <p className="text-xs text-brand-muted">{field.hint}</p>
                  </div>
                </div>
                {editingId !== field.id && (
                  <button
                    onClick={() => handleEdit(field)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-brand-dark hover:bg-brand-light rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line" />
                    {t('admin_edit')}
                  </button>
                )}
              </div>
              <div className="px-5 py-4">
                {editingId === field.id ? (
                  <div className="space-y-3">
                    {field.type === 'textarea' ? (
                      <textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        rows={4}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint resize-none transition-colors"
                        placeholder={field.placeholder}
                      />
                    ) : (
                      <input
                        type={field.type}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                        placeholder={field.placeholder}
                      />
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSaveField}
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-mint text-white text-sm font-medium rounded-lg hover:bg-brand-mint/90 transition-colors cursor-pointer disabled:opacity-60"
                      >
                        {saving ? (
                          <i className="ri-loader-4-line animate-spin" />
                        ) : (
                          <i className="ri-check-line" />
                        )}
                        {t('admin_save_changes')}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 text-sm text-brand-dark hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                      >
                        {t('admin_cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-brand-muted leading-relaxed">
                    {field.value || <span className="italic text-gray-400">{t('admin_no_content')}</span>}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <i className="ri-information-line text-blue-500 text-lg" />
          </div>
          <div>
            <p className="text-sm font-medium text-brand-dark">{t('admin_save_all')}</p>
            <p className="text-xs text-brand-muted">{t('admin_backend_desc')}</p>
          </div>
        </div>
        <button
          onClick={handleSaveAll}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-sm disabled:opacity-60"
        >
          {saving ? (
            <i className="ri-loader-4-line animate-spin" />
          ) : (
            <i className="ri-save-line" />
          )}
          {t('admin_save_all')}
        </button>
      </div>
    </div>
  );
}